import React from 'react';
import {
  Menu,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';

import { ScreenshotSize, StepUnit, BurstUnit, TimecodePosition } from '../types';
import { updateUserSetting } from '../services/userSettingsService';
import { useAuth } from '../contexts/AuthContext';

interface SettingsMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  timecodePosition: TimecodePosition;
  setTimecodePosition: (pos: TimecodePosition) => void;
  showTimecode: boolean; // 🔧 新增：時間碼顯示開關
  setShowTimecode: (show: boolean) => void; // 🔧 新增
  showTimecodeBackground: boolean; // 🔧 新增：時間碼背景開關
  setShowTimecodeBackground: (show: boolean) => void; // 🔧 新增
  fastForwardSeconds: number;
  setFastForwardSeconds: (n: number) => void;
  stepUnit: StepUnit;
  setStepUnit: (u: StepUnit) => void;
  playbackDuration: number;
  setPlaybackDuration: (n: number) => void;
  screenshotSize: ScreenshotSize;
  setScreenshotSize: (s: ScreenshotSize) => void;
  showWatermarkInOutput: boolean;
  setShowWatermarkInOutput: (b: boolean) => void;
  isSoundEnabled: boolean;
  setIsSoundEnabled: (b: boolean) => void;
  enableFadeEffect: boolean;
  setEnableFadeEffect: (b: boolean) => void;
  subtitleFillLines: number;
  setSubtitleFillLines: (lines: number) => void;
  onCleanupDuplicateVbids?: () => void;
  onApiMonitorOpen?: () => void;
}

const SettingsMenu: React.FC<SettingsMenuProps> = ({
  anchorEl,
  open,
  onClose,
  timecodePosition,
  setTimecodePosition,
  showTimecode, // 🔧 新增
  setShowTimecode, // 🔧 新增
  showTimecodeBackground, // 🔧 新增
  setShowTimecodeBackground, // 🔧 新增
  fastForwardSeconds,
  setFastForwardSeconds,
  stepUnit,
  setStepUnit,
  playbackDuration,
  setPlaybackDuration,
  screenshotSize,
  setScreenshotSize,
  showWatermarkInOutput,
  setShowWatermarkInOutput,
  isSoundEnabled,
  setIsSoundEnabled,
  enableFadeEffect,
  setEnableFadeEffect,
  subtitleFillLines,
  setSubtitleFillLines,
  onCleanupDuplicateVbids,
  onApiMonitorOpen
}) => {
  const { user } = useAuth();
  
  return (
  <Menu
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    sx={{ zIndex: 9999 }}
  >
    <Box sx={{ p: 2, minWidth: 300 }}>
      <Typography variant="h6" gutterBottom>
        設定
      </Typography>

      {/* 時間碼設定 */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          時間碼
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
            <Button
              variant={timecodePosition === 'topLeft' ? 'contained' : 'outlined'}
              size="small"
              onClick={async () => {
                console.log('🎯 SettingsMenu: 點擊左上，更新 timecodePosition');
                setTimecodePosition('topLeft' as TimecodePosition);
                // ☁️ 自動同步到 Firestore（登入用戶）
                await updateUserSetting(user?.uid, 'timecodePosition', 'topLeft');
                // 🔧 觸發自定義事件，通知 MainPage 同步
                window.dispatchEvent(new Event('localStorageChanged'));
              }}
            >
              左上
            </Button>
            <Button
              variant={timecodePosition === 'topCenter' ? 'contained' : 'outlined'}
              size="small"
              onClick={async () => {
                console.log('🎯 SettingsMenu: 點擊中上，更新 timecodePosition');
                setTimecodePosition('topCenter' as TimecodePosition);
                // ☁️ 自動同步到 Firestore（登入用戶）
                await updateUserSetting(user?.uid, 'timecodePosition', 'topCenter');
                // 🔧 觸發自定義事件，通知 MainPage 同步
                window.dispatchEvent(new Event('localStorageChanged'));
              }}
            >
              中上
            </Button>
            <Button
              variant={timecodePosition === 'topRight' ? 'contained' : 'outlined'}
              size="small"
              onClick={async () => {
                console.log('🎯 SettingsMenu: 點擊右上，更新 timecodePosition');
                setTimecodePosition('topRight' as TimecodePosition);
                // ☁️ 自動同步到 Firestore（登入用戶）
                await updateUserSetting(user?.uid, 'timecodePosition', 'topRight');
                // 🔧 觸發自定義事件，通知 MainPage 同步
                window.dispatchEvent(new Event('localStorageChanged'));
              }}
            >
              右上
            </Button>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
            <Button
              variant={timecodePosition === 'bottomLeft' ? 'contained' : 'outlined'}
              size="small"
              onClick={async () => {
                console.log('🎯 SettingsMenu: 點擊左下，更新 timecodePosition');
                setTimecodePosition('bottomLeft' as TimecodePosition);
                // ☁️ 自動同步到 Firestore（登入用戶）
                await updateUserSetting(user?.uid, 'timecodePosition', 'bottomLeft');
                // 🔧 觸發自定義事件，通知 MainPage 同步
                window.dispatchEvent(new Event('localStorageChanged'));
              }}
            >
              左下
            </Button>
            <Button
              variant={timecodePosition === 'bottomCenter' ? 'contained' : 'outlined'}
              size="small"
              onClick={async () => {
                console.log('🎯 SettingsMenu: 點擊中下，更新 timecodePosition');
                setTimecodePosition('bottomCenter' as TimecodePosition);
                // ☁️ 自動同步到 Firestore（登入用戶）
                await updateUserSetting(user?.uid, 'timecodePosition', 'bottomCenter');
                // 🔧 觸發自定義事件，通知 MainPage 同步
                window.dispatchEvent(new Event('localStorageChanged'));
              }}
            >
              中下
            </Button>
            <Button
              variant={timecodePosition === 'bottomRight' ? 'contained' : 'outlined'}
              size="small"
              onClick={async () => {
                console.log('🎯 SettingsMenu: 點擊右下，更新 timecodePosition');
                setTimecodePosition('bottomRight' as TimecodePosition);
                // ☁️ 自動同步到 Firestore（登入用戶）
                await updateUserSetting(user?.uid, 'timecodePosition', 'bottomRight');
                // 🔧 觸發自定義事件，通知 MainPage 同步
                window.dispatchEvent(new Event('localStorageChanged'));
              }}
            >
              右下
            </Button>
          </Box>
          
          {/* 🔧 新增：時間碼顯示開關 */}
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showTimecode}
                  onChange={async (e) => {
                    console.log('🎯 SettingsMenu: 切換時間碼顯示:', e.target.checked);
                    setShowTimecode(e.target.checked);
                    // ☁️ 自動同步到 Firestore（登入用戶）
                    await updateUserSetting(user?.uid, 'showTimecode', e.target.checked);
                    // 🔧 觸發自定義事件，通知 MainPage 同步
                    localStorage.setItem('showTimecode', String(e.target.checked));
                    window.dispatchEvent(new Event('localStorageChanged'));
                  }}
                />
              }
              label="顯示時間碼"
            />
          </Box>
          
          {/* 🔧 新增：時間碼背景開關 */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={showTimecodeBackground}
                  onChange={async (e) => {
                    console.log('🎯 SettingsMenu: 切換時間碼背景:', e.target.checked);
                    setShowTimecodeBackground(e.target.checked);
                    // ☁️ 自動同步到 Firestore（登入用戶）
                    await updateUserSetting(user?.uid, 'showTimecodeBackground', e.target.checked);
                    // 🔧 觸發自定義事件，通知 MainPage 同步
                    localStorage.setItem('showTimecodeBackground', String(e.target.checked));
                    window.dispatchEvent(new Event('localStorageChanged'));
                  }}
                />
              }
              label="顯示時間碼背景"
            />
          </Box>
        </Box>
      </Box>

      {/* 播放設定 */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          播放設定
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="fast-forward-label">快進/倒帶秒數</InputLabel>
            <Select
              id="fast-forward-select"
              name="fastForwardSeconds"
              value={fastForwardSeconds}
              onChange={(e) => setFastForwardSeconds(Number(e.target.value))}
              label="快進/倒帶秒數"
              labelId="fast-forward-label"
            >
              <MenuItem value={5}>5秒</MenuItem>
              <MenuItem value={10}>10秒</MenuItem>
              <MenuItem value={15}>15秒</MenuItem>
              <MenuItem value={30}>30秒</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="step-unit-label">步進單位</InputLabel>
            <Select
              id="step-unit-select"
              name="stepUnit"
              value={stepUnit}
              onChange={(e) => setStepUnit(e.target.value as StepUnit)}
              label="步進單位"
              labelId="step-unit-label"
            >
              <MenuItem value="second">秒</MenuItem>
              <MenuItem value="frame">幀</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="playback-duration-label">回放秒數</InputLabel>
            <Select
              id="playback-duration-select"
              name="playbackDuration"
              value={playbackDuration}
              onChange={(e) => setPlaybackDuration(Number(e.target.value))}
              label="回放秒數"
              labelId="playback-duration-label"
            >
              <MenuItem value={5}>5秒</MenuItem>
              <MenuItem value={10}>10秒</MenuItem>
              <MenuItem value={30}>30秒</MenuItem>
              <MenuItem value={60}>60秒</MenuItem>
              <MenuItem value={90}>90秒</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* 其他設定 */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          其他設定
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="subtitle-fill-lines-label">字幕填充行數</InputLabel>
            <Select
              id="subtitle-fill-lines-select"
              name="subtitleFillLines"
              value={subtitleFillLines}
              onChange={(e) => setSubtitleFillLines(Number(e.target.value))}
              label="字幕填充行數"
              labelId="subtitle-fill-lines-label"
            >
              <MenuItem value={2}>2 行 (前0行 + 當前行 + 後1行) - 預設</MenuItem>
              <MenuItem value={3}>3 行 (前1行 + 當前行 + 後1行)</MenuItem>
              <MenuItem value={4}>4 行 (前1行 + 當前行 + 後2行)</MenuItem>
              <MenuItem value={5}>5 行 (前1行 + 當前行 + 後3行)</MenuItem>
              <MenuItem value={6}>6 行 (前1行 + 當前行 + 後4行)</MenuItem>
              <MenuItem value={7}>7 行 (前1行 + 當前行 + 後5行)</MenuItem>
              <MenuItem value={8}>8 行 (前1行 + 當前行 + 後6行)</MenuItem>
              <MenuItem value={9}>9 行 (前1行 + 當前行 + 後7行)</MenuItem>
              <MenuItem value={10}>10 行 (前1行 + 當前行 + 後8行)</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="screenshot-size-label">截圖尺寸</InputLabel>
            <Select
              id="screenshot-size-select"
              name="screenshotSize"
              value={screenshotSize}
              onChange={(e) => setScreenshotSize(e.target.value as ScreenshotSize)}
              label="截圖尺寸"
              labelId="screenshot-size-label"
            >
              <MenuItem value="1:1">1:1 (原始尺寸)</MenuItem>
              <MenuItem value="1/2">1/2 (原始尺寸的1/2)</MenuItem>
              <MenuItem value="1/4">1/4 (原始尺寸的1/4)</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={enableFadeEffect}
                onChange={(e) => setEnableFadeEffect(e.target.checked)}
              />
            }
            label="Fade in/out 效果"
          />
          <FormControlLabel
            control={
              <Switch
                checked={showWatermarkInOutput}
                onChange={(e) => setShowWatermarkInOutput(e.target.checked)}
              />
            }
            label="截圖合併時間碼浮水印"
          />
          <FormControlLabel
            control={
              <Switch
                checked={isSoundEnabled}
                onChange={(e) => setIsSoundEnabled(e.target.checked)}
              />
            }
            label="按鈕音效"
          />
          {onCleanupDuplicateVbids && (
            <Button variant="outlined" size="small" onClick={onCleanupDuplicateVbids}>
              清理重複 vbids 資料
            </Button>
          )}
        </Box>
      </Box>

      {/* 系統監控 */}
      {onApiMonitorOpen && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            系統監控
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => {
                onApiMonitorOpen();
                onClose();
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              📊 API 請求監控
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  </Menu>
  );
};

export default SettingsMenu;